/**
 * NOS-46: Event create/edit form with BlockEditor for description.
 * Gated by can_manage_events.
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import debounce from "lodash.debounce"
import { Loader2, Users, Mail, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import {
  useEvent,
  useCreateEvent,
  useUpdateEvent,
  useDeleteEvent,
  useEventAttendees,
} from "@/hooks/useEvents"
import { BlockEditor } from "@/components/website-builder/BlockEditor"
import { getDefaultBlockContent } from "@/components/website-builder/blocks/registry"
import type { BlockType } from "@/components/website-builder/blocks/types"
import type { PageSection } from "@/templates/types"
import { generateSlug } from "@/lib/slugUtils"
import { toast } from "sonner"
import type { Json } from "@/types/database"
import { RegistrationSettingsPanel } from "@/components/events/RegistrationSettingsPanel"
import { TicketTypesEditor } from "@/components/events/TicketTypesEditor"

function descriptionSectionsToBlocks(
  sections: Json,
  orgId: string
): PageSection[] {
  const arr = Array.isArray(sections) ? sections : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

function blocksToDescriptionSections(blocks: PageSection[]): Json {
  return blocks
    .sort((a, b) => a.order_index - b.order_index)
    .map((b) => ({
      id: b.id,
      section_type: b.section_type,
      order_index: b.order_index,
      content: (b.content ?? {}) as Record<string, unknown>,
    })) as Json
}

export function EventEditorPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const isNew =
    location.pathname.endsWith("/events/new") || eventId === "new"

  const { event, loading: eventLoading, refresh } = useEvent(
    orgId,
    isNew ? null : eventId ?? null
  )
  const { createEvent } = useCreateEvent(orgId)
  const { updateEvent } = useUpdateEvent(orgId)
  const { deleteEvent } = useDeleteEvent(orgId)
  const { attendees, loading: attendeesLoading } = useEventAttendees(
    orgId,
    isNew ? null : eventId ?? null
  )

  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [locationName, setLocationName] = useState("")
  const [locationAddress, setLocationAddress] = useState("")
  const [locationType, setLocationType] = useState<"in_person" | "virtual" | "hybrid">("in_person")
  const [virtualUrl, setVirtualUrl] = useState("")
  const [coverImage, setCoverImage] = useState("")
  const [maxAttendees, setMaxAttendees] = useState("")
  const [isPublished, setIsPublished] = useState(false)
  const [registrationEnabled, setRegistrationEnabled] = useState(false)
  const [waitlistEnabled, setWaitlistEnabled] = useState(false)
  const [registrationDeadline, setRegistrationDeadline] = useState("")
  const [registrationCapacity, setRegistrationCapacity] = useState("")
  const [registrationSectionOpen, setRegistrationSectionOpen] = useState(false)
  const [blocks, setBlocks] = useState<PageSection[]>([])
  // NOS-249: per-event SEO overrides (fall back to title/body/cover on the
  // public site, then the generated branded OG default)
  const [seoTitle, setSeoTitle] = useState("")
  const [seoDescription, setSeoDescription] = useState("")
  const [ogImage, setOgImage] = useState("")

  const blocksForEditor = useMemo(
    () =>
      descriptionSectionsToBlocks(event?.description_sections ?? [], orgId ?? ""),
    [event?.description_sections, orgId]
  )

  useEffect(() => {
    if (event) {
      setTitle(event.title)
      setSlug(event.slug)
      setStartDate(event.start_date.slice(0, 16))
      setEndDate(event.end_date ? event.end_date.slice(0, 16) : "")
      setLocationName(event.location_name ?? "")
      setLocationAddress(event.location_address ?? "")
      setLocationType((event.location_type ?? "in_person") as "in_person" | "virtual" | "hybrid")
      setVirtualUrl(event.virtual_url ?? "")
      setCoverImage(event.cover_image ?? "")
      setMaxAttendees(event.max_attendees != null ? String(event.max_attendees) : "")
      setIsPublished(event.is_published)
      setRegistrationEnabled(event.registration_enabled)
      setWaitlistEnabled(event.waitlist_enabled)
      setRegistrationDeadline(
        event.registration_deadline ? event.registration_deadline.slice(0, 16) : ""
      )
      setRegistrationCapacity(event.capacity != null ? String(event.capacity) : "")
      setRegistrationSectionOpen(event.registration_enabled)
      setBlocks(
        descriptionSectionsToBlocks(event.description_sections ?? [], orgId ?? "")
      )
      setSeoTitle(event.seo_title ?? "")
      setSeoDescription(event.seo_description ?? "")
      setOgImage(event.og_image ?? "")
    } else if (isNew) {
      setTitle("")
      setSlug("")
      setStartDate("")
      setEndDate("")
      setLocationName("")
      setLocationAddress("")
      setLocationType("in_person")
      setVirtualUrl("")
      setCoverImage("")
      setMaxAttendees("")
      setIsPublished(false)
      setRegistrationEnabled(false)
      setWaitlistEnabled(false)
      setRegistrationDeadline("")
      setRegistrationCapacity("")
      setRegistrationSectionOpen(false)
      setBlocks([])
      setSeoTitle("")
      setSeoDescription("")
      setOgImage("")
    }
  }, [event, isNew, orgId])

  const handleTitleChange = (v: string) => {
    setTitle(v)
    if (slug === "" || (event && slug === generateSlug(event.title))) {
      setSlug(generateSlug(v))
    }
  }

  const saveDraft = useCallback(
    async (updates: {
      title?: string
      slug?: string
      start_date?: string
      end_date?: string | null
      location_name?: string | null
      location_address?: string | null
      location_type?: "in_person" | "virtual" | "hybrid" | null
      virtual_url?: string | null
      cover_image?: string | null
      max_attendees?: number | null
      is_published?: boolean
      registration_enabled?: boolean
      waitlist_enabled?: boolean
      registration_deadline?: string | null
      capacity?: number | null
      description_sections?: Json
      seo_title?: string | null
      seo_description?: string | null
      og_image?: string | null
    }): Promise<boolean> => {
      if (!orgId || isNew || !eventId) return false
      const ok = await updateEvent(eventId, updates)
      if (ok) refresh()
      return ok
    },
    [orgId, isNew, eventId, updateEvent, refresh]
  )

  const debouncedSave = useMemo(
    () =>
      debounce(
        (updates: Parameters<typeof saveDraft>[0]) => {
          saveDraft(updates)
        },
        800
      ),
    [saveDraft]
  )

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave])

  const handleBlocksChange = (newBlocks: PageSection[]) => {
    setBlocks(newBlocks)
    if (!isNew && eventId) {
      debouncedSave({ description_sections: blocksToDescriptionSections(newBlocks) })
    }
  }

  const handleUpdateBlock = (sectionId: string, content: Record<string, unknown>) => {
    const next = blocks.map((b) =>
      b.id === sectionId ? { ...b, content } : b
    )
    handleBlocksChange(next)
  }

  const handleReorderBlocks = (newOrder: PageSection[]) => {
    const reindexed = newOrder.map((b, i) => ({ ...b, order_index: i }))
    handleBlocksChange(reindexed)
  }

  const handleAddBlock = (type: BlockType, afterIndex: number) => {
    const newBlock: PageSection = {
      id: crypto.randomUUID(),
      section_type: type,
      order_index: afterIndex + 1,
      content: getDefaultBlockContent(type) as Record<string, unknown>,
      page_id: "",
      org_id: orgId ?? "",
      is_visible: true,
    }
    const reindexed = [
      ...blocks.slice(0, afterIndex + 1),
      newBlock,
      ...blocks.slice(afterIndex + 1),
    ].map((b, i) => ({ ...b, order_index: i }))
    handleBlocksChange(reindexed)
  }

  const handleRemoveBlock = (sectionId: string) => {
    handleBlocksChange(blocks.filter((b) => b.id !== sectionId))
  }

  const handleSaveMetadata = async () => {
    debouncedSave.cancel()
    const ok = await saveDraft({
      title,
      slug,
      start_date: startDate ? `${startDate}:00.000Z` : new Date().toISOString(),
      end_date: endDate ? `${endDate}:00.000Z` : null,
      location_name: locationName.trim() || null,
      location_address: locationAddress.trim() || null,
      location_type: locationType,
      virtual_url: virtualUrl.trim() || null,
      cover_image: coverImage.trim() || null,
      max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : null,
      is_published: isPublished,
      registration_enabled: registrationEnabled,
      waitlist_enabled: waitlistEnabled,
      registration_deadline: registrationDeadline ? `${registrationDeadline}:00.000Z` : null,
      capacity: registrationCapacity ? parseInt(registrationCapacity, 10) : null,
      seo_title: seoTitle.trim() || null,
      seo_description: seoDescription.trim() || null,
      og_image: ogImage.trim() || null,
    })
    if (ok) toast.success("Saved")
  }

  const handlePublishToggle = async () => {
    const next = !isPublished
    setIsPublished(next)
    if (isNew) return
    debouncedSave.cancel()
    const ok = await saveDraft({ is_published: next })
    if (ok) toast.success(next ? "Published" : "Unpublished")
  }

  const handleCreate = async () => {
    const created = await createEvent({
      title: title.trim() || "Untitled Event",
      slug: slug.trim() || generateSlug(title) || "untitled-event",
      start_date: startDate ? `${startDate}:00.000Z` : new Date().toISOString(),
      end_date: endDate ? `${endDate}:00.000Z` : null,
      location_name: locationName.trim() || null,
      location_address: locationAddress.trim() || null,
      location_type: locationType,
      virtual_url: virtualUrl.trim() || null,
      cover_image: coverImage.trim() || null,
      max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : null,
      is_published: isPublished,
      registration_enabled: registrationEnabled,
      waitlist_enabled: waitlistEnabled,
      registration_deadline: registrationDeadline ? `${registrationDeadline}:00.000Z` : null,
      capacity: registrationCapacity ? parseInt(registrationCapacity, 10) : null,
      created_by: user?.id ?? null,
      seo_title: seoTitle.trim() || null,
      seo_description: seoDescription.trim() || null,
      og_image: ogImage.trim() || null,
    })
    if (created) {
      toast.success(isPublished ? "Event created and published" : "Event created")
      navigate(`/dashboard/events/${created.id}/edit`)
    } else {
      toast.error("Failed to create event")
    }
  }

  const handleSaveAndReturn = async () => {
    debouncedSave.cancel()
    const ok = await saveDraft({
      title,
      slug,
      start_date: startDate ? `${startDate}:00.000Z` : new Date().toISOString(),
      end_date: endDate ? `${endDate}:00.000Z` : null,
      location_name: locationName.trim() || null,
      location_address: locationAddress.trim() || null,
      location_type: locationType,
      virtual_url: virtualUrl.trim() || null,
      cover_image: coverImage.trim() || null,
      max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : null,
      is_published: isPublished,
      registration_enabled: registrationEnabled,
      waitlist_enabled: waitlistEnabled,
      registration_deadline: registrationDeadline ? `${registrationDeadline}:00.000Z` : null,
      capacity: registrationCapacity ? parseInt(registrationCapacity, 10) : null,
      description_sections: blocksToDescriptionSections(blocks),
      seo_title: seoTitle.trim() || null,
      seo_description: seoDescription.trim() || null,
      og_image: ogImage.trim() || null,
    })
    if (ok) {
      toast.success("Saved")
      navigate("/dashboard/events")
    } else {
      toast.error("Failed to save")
    }
  }

  const handleDelete = async () => {
    if (!eventId) return
    const ok = await deleteEvent(eventId)
    if (ok) {
      toast.success("Event deleted")
      navigate("/dashboard/events")
    } else {
      toast.error("Failed to delete event")
    }
  }

  if (eventLoading && !isNew) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isNew && !eventLoading && !event) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Event not found.</p>
        <Button asChild variant="outline">
          <Link to="/dashboard/events">Back to Events</Link>
        </Button>
      </div>
    )
  }

  return (
    <PermissionGate permission="can_manage_events" fallback={<Navigate to="/dashboard" replace />}>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard/events">← Back</Link>
            </Button>
            <h1 className="text-2xl font-bold">
              {isNew ? "New event" : "Edit event"}
            </h1>
            {event && !event.is_published && (
              <span className="text-sm text-muted-foreground">Draft</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isNew ? (
              <Button onClick={handleCreate} disabled={!title.trim() || !startDate}>
                {isPublished ? "Create and publish" : "Next: Add description"}
              </Button>
            ) : (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={!eventId}
                >
                  Delete
                </Button>
                <Button variant="outline" size="sm" onClick={handleSaveAndReturn}>
                  Save and return
                </Button>
                <Button onClick={handleSaveMetadata}>Save</Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Event title"
                onBlur={() => !isNew && debouncedSave({ title, slug })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="event-slug"
                onBlur={() => !isNew && debouncedSave({ slug })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start date & time</Label>
                <Input
                  id="start_date"
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onBlur={() =>
                    !isNew &&
                    debouncedSave({
                      start_date: startDate ? `${startDate}:00.000Z` : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End date & time (optional)</Label>
                <Input
                  id="end_date"
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onBlur={() =>
                    !isNew &&
                    debouncedSave({
                      end_date: endDate ? `${endDate}:00.000Z` : null,
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location_type">Location type</Label>
              <Select
                value={locationType}
                onValueChange={(v) => {
                  setLocationType(v as "in_person" | "virtual" | "hybrid")
                  if (!isNew)
                    debouncedSave({
                      location_type: v as "in_person" | "virtual" | "hybrid",
                    })
                }}
              >
                <SelectTrigger id="location_type">
                  <SelectValue placeholder="Location type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(locationType === "in_person" || locationType === "hybrid") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="location_name">Venue / location name</Label>
                  <Input
                    id="location_name"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    placeholder="e.g. Community Center"
                    onBlur={() => !isNew && debouncedSave({ location_name: locationName })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location_address">Address</Label>
                  <Input
                    id="location_address"
                    value={locationAddress}
                    onChange={(e) => setLocationAddress(e.target.value)}
                    placeholder="123 Main St, City, State"
                    onBlur={() => !isNew && debouncedSave({ location_address: locationAddress })}
                  />
                </div>
              </>
            )}
            {(locationType === "virtual" || locationType === "hybrid") && (
              <div className="space-y-2">
                <Label htmlFor="virtual_url">Virtual meeting URL</Label>
                <Input
                  id="virtual_url"
                  value={virtualUrl}
                  onChange={(e) => setVirtualUrl(e.target.value)}
                  placeholder="https://..."
                  onBlur={() => !isNew && debouncedSave({ virtual_url: virtualUrl })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="cover_image">Cover image URL</Label>
              <Input
                id="cover_image"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="https://..."
                onBlur={() => !isNew && debouncedSave({ cover_image: coverImage })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_attendees">Max attendees (optional)</Label>
              <Input
                id="max_attendees"
                type="number"
                min={0}
                value={maxAttendees}
                onChange={(e) => setMaxAttendees(e.target.value)}
                placeholder="Leave empty for unlimited"
                onBlur={() =>
                  !isNew &&
                  debouncedSave({
                    max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : null,
                  })
                }
              />
            </div>

            <div className="space-y-2 rounded-lg border">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left font-medium hover:bg-muted/50"
                onClick={() => setRegistrationSectionOpen((o) => !o)}
                aria-expanded={registrationSectionOpen}
              >
                <span>Registration</span>
                {registrationSectionOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
              {registrationSectionOpen && (
                <div className="space-y-6 border-t px-4 pb-4 pt-4">
                  <RegistrationSettingsPanel
                    registrationEnabled={registrationEnabled}
                    waitlistEnabled={waitlistEnabled}
                    registrationDeadline={registrationDeadline}
                    capacity={registrationCapacity}
                    onRegistrationEnabledChange={(v) => {
                      setRegistrationEnabled(v)
                      if (v) setRegistrationSectionOpen(true)
                      if (!isNew) {
                        debouncedSave.cancel()
                        void saveDraft({ registration_enabled: v })
                      }
                    }}
                    onWaitlistEnabledChange={(v) => {
                      setWaitlistEnabled(v)
                      if (!isNew) {
                        debouncedSave.cancel()
                        void saveDraft({ waitlist_enabled: v })
                      }
                    }}
                    onRegistrationDeadlineChange={setRegistrationDeadline}
                    onCapacityChange={setRegistrationCapacity}
                    onRegistrationDeadlineBlur={() => {
                      if (!isNew) {
                        debouncedSave.cancel()
                        void saveDraft({
                          registration_deadline: registrationDeadline
                            ? `${registrationDeadline}:00.000Z`
                            : null,
                        })
                      }
                    }}
                    onCapacityBlur={() => {
                      if (!isNew) {
                        debouncedSave.cancel()
                        void saveDraft({
                          capacity: registrationCapacity
                            ? parseInt(registrationCapacity, 10)
                            : null,
                        })
                      }
                    }}
                  />
                  {registrationEnabled && orgId && (
                    <div className="space-y-2">
                      <Label>Ticket types</Label>
                      <TicketTypesEditor
                        eventId={eventId ?? "new"}
                        orgId={orgId}
                        disabled={isNew || !eventId}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isNew && (
              <div className="space-y-4">
                <Label>Description</Label>
                <BlockEditor
                  blocks={blocks.length ? blocks : blocksForEditor}
                  onUpdateBlock={handleUpdateBlock}
                  onReorderBlocks={handleReorderBlocks}
                  onAddBlock={handleAddBlock}
                  onRemoveBlock={handleRemoveBlock}
                  lockedTypes={[]}
                  canAddBlocks
                />
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="publish">Publish</Label>
                <Switch
                  id="publish"
                  checked={isPublished}
                  onCheckedChange={handlePublishToggle}
                />
              </div>
            </div>

            <div className="space-y-4 rounded-lg border p-4">
              <p className="text-sm font-medium">SEO</p>
              <div className="space-y-2">
                <Label htmlFor="seo_title">SEO title</Label>
                <Input
                  id="seo_title"
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder={title ? `${title} | …` : "Override for search results"}
                  onBlur={() => !isNew && debouncedSave({ seo_title: seoTitle.trim() || null })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="seo_description">SEO description</Label>
                <Textarea
                  id="seo_description"
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder="Defaults to the event description"
                  rows={2}
                  onBlur={() =>
                    !isNew && debouncedSave({ seo_description: seoDescription.trim() || null })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="og_image">Social share image URL</Label>
                <Input
                  id="og_image"
                  value={ogImage}
                  onChange={(e) => setOgImage(e.target.value)}
                  placeholder="Defaults to the cover image"
                  onBlur={() => !isNew && debouncedSave({ og_image: ogImage.trim() || null })}
                />
              </div>
            </div>

            {!isNew && event && (
              <div className="space-y-4 rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-medium">Attendees</h3>
                </div>
                {attendeesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : attendees.filter((a) => a.status === "registered").length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No attendees yet. Share your event page for people to RSVP.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {attendees
                      .filter((a) => a.status === "registered")
                      .map((a) => {
                        const c = a.contacts as { id: string; email: string; name: string | null } | null
                        const display = c?.name?.trim() || c?.email || "Unknown"
                        return (
                          <li key={a.id}>
                            <Link
                              to={`/dashboard/contacts/${a.contact_id}`}
                              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                            >
                              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="truncate">{display}</span>
                              {a.guest_count > 0 && (
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                  +{a.guest_count}
                                </span>
                              )}
                            </Link>
                          </li>
                        )
                      })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PermissionGate>
  )
}
